import { GameObjects, Scene } from 'phaser';
import { UnitType, UnitStats } from '../config/BalanceConfig';
import { COLORS, SIZES, UNIT_STATS } from '../config/Constants';

export class Unit extends GameObjects.GameObject {
  public body: any;
  public graphics: GameObjects.Shape;
  private unitType: UnitType;
  private stats: UnitStats;
  private kills: number = 0;
  private lastAttackTime: number = 0;
  private isMoving: boolean = false;

  constructor(scene: Scene, x: number, y: number, type: UnitType) {
    super(scene, 'Unit');
    
    this.unitType = type;
    this.stats = this.getInitialStats(type);
    
    this.graphics = this.createUnitGraphic(x, y, type);
    scene.physics.add.existing(this.graphics);
    this.body = this.graphics.body;
    
    // Store position for collision detection
    this.graphics.setPosition(x, y);
    
    scene.add.existing(this);
  }

  public get x(): number {
    return this.graphics?.x || 0;
  }

  public get y(): number {
    return this.graphics?.y || 0;
  }

  public set x(value: number) {
    if (this.graphics) this.graphics.x = value;
  }

  public set y(value: number) {
    if (this.graphics) this.graphics.y = value;
  }

  private getInitialStats(type: UnitType): UnitStats {
    switch (type) {
      case UnitType.TANK:
        return {
          health: UNIT_STATS.PLAYER_TANK.HEALTH,
          maxHealth: UNIT_STATS.PLAYER_TANK.HEALTH,
          damage: UNIT_STATS.PLAYER_TANK.DAMAGE,
          speed: UNIT_STATS.PLAYER_TANK.SPEED,
          range: UNIT_STATS.PLAYER_TANK.RANGE // Minimal range for melee
        };
      case UnitType.RANGED:
        return {
          health: UNIT_STATS.PLAYER_RANGED.HEALTH,
          maxHealth: UNIT_STATS.PLAYER_RANGED.HEALTH,
          damage: UNIT_STATS.PLAYER_RANGED.DAMAGE,
          speed: UNIT_STATS.PLAYER_RANGED.SPEED,
          range: SIZES.UNIT_RANGED_RADIUS * 8 // 8 times unit image radius for better ranged combat
        };
      case UnitType.CHARGER:
        return {
          health: UNIT_STATS.PLAYER_CHARGER.HEALTH,
          maxHealth: UNIT_STATS.PLAYER_CHARGER.HEALTH,
          damage: UNIT_STATS.PLAYER_CHARGER.DAMAGE,
          speed: UNIT_STATS.PLAYER_CHARGER.SPEED,
          range: UNIT_STATS.PLAYER_CHARGER.RANGE
        };
      default:
        return {
          health: 50,
          maxHealth: 50,
          damage: 15,
          speed: 50,
          range: 50
        };
    }
  }

  private createUnitGraphic(x: number, y: number, type: UnitType): GameObjects.Shape {
    switch (type) {
      case UnitType.TANK:
        return this.scene.add.rectangle(x, y, SIZES.UNIT_TANK_WIDTH, SIZES.UNIT_TANK_HEIGHT, COLORS.UNIT_TANK);
      case UnitType.RANGED:
        return this.scene.add.circle(x, y, SIZES.UNIT_RANGED_RADIUS, COLORS.UNIT_RANGED);
      case UnitType.CHARGER:
        const triangleSize = SIZES.UNIT_CHARGER_SIZE / 2;
        return this.scene.add.triangle(x, y, 0, -triangleSize, -triangleSize * 0.8, triangleSize * 0.8, triangleSize * 0.8, triangleSize * 0.8, COLORS.UNIT_CHARGER);
      default:
        return this.scene.add.rectangle(x, y, SIZES.UNIT_DEFAULT_SIZE, SIZES.UNIT_DEFAULT_SIZE, COLORS.UNIT_DEFAULT);
    }
  }

  public update(): void {
    // Movement logic will be handled by systems
  }

  public takeDamage(damage: number): void {
    this.stats.health -= damage;
    
    if (this.stats.health <= 0) {
      this.destroy();
    }
  }

  public addKill(): void {
    this.kills++;
    this.applyKillBonus();
  }

  private applyKillBonus(): void {
    const healthBonus = this.kills * 2;
    const damageBonus = this.kills * 1;
    
    this.stats.maxHealth += healthBonus;
    this.stats.health += healthBonus;
    this.stats.damage += damageBonus;
  }

  public getStats(): UnitStats {
    return { ...this.stats };
  }

  public getUnitType(): UnitType {
    return this.unitType;
  }

  public getKills(): number {
    return this.kills;
  }

  public getLastAttackTime(): number {
    return this.lastAttackTime;
  }

  public setLastAttackTime(time: number): void {
    this.lastAttackTime = time;
  }

  public setIsMoving(moving: boolean): void {
    this.isMoving = moving;
  }

  public getIsMoving(): boolean {
    return this.isMoving;
  }

  public canAttack(cooldown: number): boolean {
    return Date.now() - this.lastAttackTime >= cooldown;
  }

  public destroy(): void {
    this.graphics.destroy();
    super.destroy();
  }
}